import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import { DateInput, Loader, NumberInput, Tooltip } from '../../../components';
import AnimalInfo from '../../../components/animal/AnimalInfo';
import OnFarmAnimalSelector from './OnFarmAnimalSelector';
import BackButton from '../../../components/back-button/BackButton';
import SpecimenLotInfo from '../../../components/specimen-lot-info/SpecimenLotInfo';
import { RequireAlert } from '../../inventory-management/InventoryManagementCollectDeposit';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, NAVIGATION_LINK_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../constants/common';
import { toastMessages } from '../../../constants/errorMessages';
import { ON_FARM_PATHS } from '../../../constants/routePaths';
import { onFarmUsePost } from '../../../api/onFarmTransactionApi';
import { showToast } from '../../../services/toast.service';
import { downloadOnFarmTransactionExport } from '../../../services/onFarmTransactionExport.service';
import { getOnFarmSpecimensByOwnership } from '../../../services/onFarmInventory.service';
import { InventorySource, TransactionTypeEnum } from '../../../types/enums';
import { SpecimenInventory, SpecimenLocation, selectOption } from '../../../types/interfaces';
import { OnFarmUseSpecimenRequest } from '../../../types/interfaces/onFarmTransaction.interfaces';
import { dateFormatMMDDYYYY, redirectToTopOfThePage, specimenNeedsStrawDetails } from '../../../utils/commonUtils';
import './onFarmInventory.scss';

const OnFarmUse = (): JSX.Element => {
  const { animalId } = useParams();
  const initialState = { value: '', name: '' };
  const activeAccount = useAppSelector(state => state.activeAccount);

  const [childKey, setChildKey] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingSpecimens, setIsLoadingSpecimens] = useState<boolean>(false);
  const [locationUseAmounts, setLocationUseAmounts] = useState<{ key: string; location: SpecimenLocation; amount: number }[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [breedDate, setBreedDate] = useState<Date>();
  const [isValidationAlertActive, setIsValidationAlertActive] = useState<boolean>(false);
  const [selectedSpecimenIndex, setSelectedSpecimenIndex] = useState<number>(-1);
  const [selectedSpecimenValue, setSelectedSpecimenValue] = useState<selectOption>(initialState);
  const [specimenOptions, setSpecimenOptions] = useState<selectOption[]>([initialState]);
  const [specimens, setSpecimens] = useState<SpecimenInventory[]>([]);
  const [selectedSpecimens, setSelectedSpecimens] = useState<{ key: string; specimen: SpecimenInventory; quantity: number }[]>(
    [],
  );
  const [usingInventory, setUsingInventory] = useState<boolean>(true);
  const isFemale = (specimen: SpecimenInventory) => specimen.animal?.gender?.toLowerCase() === 'female';

  const getSpecimenKey = useCallback((specimen: SpecimenInventory) => {
    if (isFemale(specimen) && specimen.embryoStrawId) {
      return `embryo-${specimen.embryoStrawId}`;
    }
    return `specimen-${specimen.specimenId}`;
  }, []);

  const hasFemaleSpecimen = specimens.some(specimen => isFemale(specimen));

  const totalSelectedUseQty = selectedSpecimens.reduce((total, selectedItem) => {
    return total + (selectedItem.quantity || 0);
  }, 0);

  const totalLocationUseAmounts = locationUseAmounts.reduce((total, locationAmount) => {
    return total + (locationAmount.amount || 0);
  }, 0);

  const isSemenQuantityValid = () => {
    return selectedSpecimens.every(selectedSpecimen => {
      const specimen = selectedSpecimen.specimen;

      if (isFemale(specimen)) {
        return true;
      }

      const locationTotal = locationUseAmounts
        .filter(locationAmount => locationAmount.key === selectedSpecimen.key)
        .reduce((sum, locationAmount) => sum + (locationAmount.amount || 0), 0);

      return selectedSpecimen.quantity === locationTotal;
    });
  };

  const isValid = () => {
    return (
      !!activeAccount?.accountId &&
      !!breedDate &&
      selectedSpecimens.length > 0 &&
      totalSelectedUseQty > 0 &&
      locationUseAmounts.length > 0 &&
      totalLocationUseAmounts === totalSelectedUseQty &&
      locationUseAmounts.every(location => location.amount <= location.location.quantity) &&
      isSemenQuantityValid()
    );
  };

  const buildSpecimensPayload = (): OnFarmUseSpecimenRequest[] => {
    const specimenMap = new Map<string, OnFarmUseSpecimenRequest>();

    locationUseAmounts
      .filter(locationAmount => locationAmount.amount > 0)
      .forEach(locationAmount => {
        const selectedSpecimen = selectedSpecimens.find(selectedItem => selectedItem.key === locationAmount.key);
        const specimenId = locationAmount.location.specimenId!;
        const selectedCanisterId = locationAmount.location.storageCanisterId;
        const embryoStrawId = selectedSpecimen?.specimen?.embryoStrawId;
        const mapKey = `${specimenId}-${selectedCanisterId}`;

        const existingPayload = specimenMap.get(mapKey);

        if (!existingPayload) {
          specimenMap.set(mapKey, {
            onFarmSpecimenId: specimenId,
            selectedCanisterId,
            quantityUsed: locationAmount.amount,
            ...(embryoStrawId ? { selectedEmbryoStrawIds: [embryoStrawId] } : {}),
          });
          return;
        }

        existingPayload.quantityUsed += locationAmount.amount;
        if (embryoStrawId) {
          const strawIds = existingPayload.selectedEmbryoStrawIds ?? [];
          if (!strawIds.includes(embryoStrawId)) {
            existingPayload.selectedEmbryoStrawIds = [...strawIds, embryoStrawId];
          }
        }
      });

    return Array.from(specimenMap.values());
  };

  const buildUseNotes = (): string | undefined => {
    const noteParts = [notes.trim(), breedDate ? `Breed Date: ${breedDate.toLocaleDateString('en-US')}` : undefined].filter(
      Boolean,
    );
    return noteParts.join('\n') || undefined;
  };

  const handleSubmit = async () => {
    if (!usingInventory) return;

    setIsValidationAlertActive(true);

    const hasQuantityMismatch =
      (totalSelectedUseQty > 0 || totalLocationUseAmounts > 0) && totalLocationUseAmounts !== totalSelectedUseQty;

    const hasSemenMismatch = !isSemenQuantityValid();

    if (isValid()) {
      setUsingInventory(false);

      try {
        setIsLoading(true);

        const specimens = buildSpecimensPayload();
        const { data: onFarmTransactionId } = await onFarmUsePost(activeAccount.accountId!, {
          specimens,
          notes: buildUseNotes(),
        });

        await downloadOnFarmTransactionExport(
          {
            onFarmTransactionId,
            specimens: specimens.map(specimen => ({
              onFarmSpecimenId: specimen.onFarmSpecimenId,
              quantityUsed: specimen.quantityUsed,
              selectedCanisterId: specimen.selectedCanisterId,
              selectedEmbryoStrawIds: specimen.selectedEmbryoStrawIds,
            })),
          },
          TransactionTypeEnum.Use,
        );

        showToast.success(toastMessages.USE_HAS_BEEN_SUBMITTED);
        redirectToTopOfThePage();
        clearForm();
      } catch (error: any) {
        if (error?.response?.status === 409) {
          try {
            const detailObject = JSON.parse(error.response.data.detail);
            showToast.error(detailObject.ErrMsg || toastMessages.FAILED_TO_SUBMIT_USE);
          } catch {
            showToast.error(toastMessages.FAILED_TO_SUBMIT_USE);
          }
        } else {
          showToast.error(toastMessages.FAILED_TO_SUBMIT_USE);
        }
      } finally {
        setUsingInventory(true);
        setIsLoading(false);
      }
    } else if (hasQuantityMismatch || hasSemenMismatch) {
      setUsingInventory(true);
      showToast.error(toastMessages.TOTAL_QTY_MISMATCH);
    } else {
      showToast.error(toastMessages.MISSING_REQUIRED_FIELDS);
    }
  };

  const clearForm = () => {
    setSpecimens([]);
    setSelectedSpecimens([]);
    setLocationUseAmounts([]);
    setNotes('');
    setBreedDate(undefined);
    setIsValidationAlertActive(false);
    setChildKey(prev => prev + 1);
    setSelectedSpecimenIndex(-1);
    setSelectedSpecimenValue(initialState);
  };

  const fetchSpecimens = useCallback(async () => {
    if (!animalId) {
      setSpecimens([]);
      setSpecimenOptions([initialState]);
      return;
    }

    setIsLoadingSpecimens(true);
    try {
      setSelectedSpecimens([]);
      setLocationUseAmounts([]);
      setSelectedSpecimenValue(initialState);
      setSelectedSpecimenIndex(-1);

      const { data: farmUseSpecimens } = await getOnFarmSpecimensByOwnership(animalId, activeAccount?.accountId);
      const flattenedSpecimens = farmUseSpecimens.flatMap(specimen => {
        if (isFemale(specimen) && specimen.embryoStraws?.length) {
          return specimen.embryoStraws.map(straw => ({
            ...specimen,
            embryoStrawCode: straw.embryoStrawCode,
            embryoStrawNumber: straw.embryoStrawNumber,
            embryoStrawId: straw.embryoStrawId,
            quantityAvailable: straw.embryoCountPerStraw ?? 0,
            embryoGrade: straw.embryoGrade,
            embryoStage: straw.embryoStage,
            specimenLocations: (specimen.specimenLocations ?? []).filter(
              location => !straw.storageCanisterId || location.storageCanisterId === straw.storageCanisterId,
            ),
          }));
        }
        return specimen;
      });

      setSpecimens(flattenedSpecimens);
      setSpecimenOptions(
        flattenedSpecimens.map((specimen, index) => ({
          name: `${specimen.animal?.code}-${specimen.animal?.name}-${new Date(specimen.freezeDate).toLocaleDateString()} - ${
            specimen?.lotNumber ? `${specimen.specimenId} (${specimen.lotNumber})` : specimen.specimenId
          }`,
          value: `${index}`,
        })),
      );
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoadingSpecimens(false);
    }
  }, [animalId, activeAccount?.accountId]);

  useEffect(() => {
    fetchSpecimens();
  }, [fetchSpecimens]);

  const renderStrawLotContent = (specimen: SpecimenInventory) => {
    if (specimen.embryoStrawCode) {
      return (
        <>
          {specimen.embryoStrawCode}
          <Tooltip>
            {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${specimen.embryoGrade || 'NA'}`} <br />
            {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${specimen.embryoStage || 'NA'}`}
          </Tooltip>
        </>
      );
    }

    return '-';
  };

  useEffect(() => {
    setLocationUseAmounts(previousLocations => {
      const updatedLocations = [...previousLocations];

      selectedSpecimens.forEach(({ specimen, key: specimenKey }) => {
        specimen.specimenLocations?.forEach(specimenLocation => {
          const alreadyExists = updatedLocations.some(
            existingLocation =>
              existingLocation.key === specimenKey &&
              existingLocation.location.storageCanisterId === specimenLocation.storageCanisterId,
          );

          if (!alreadyExists) {
            updatedLocations.push({
              key: specimenKey,
              location: specimenLocation,
              amount: isFemale(specimen) ? specimen.quantityAvailable ?? 0 : 0,
            });
          }
        });
      });

      return updatedLocations.filter(existingLocation =>
        selectedSpecimens.some(selectedSpecimen => selectedSpecimen.key === existingLocation.key),
      );
    });
  }, [selectedSpecimens]);

  const handleSpecimenRowClass = (specimen: SpecimenInventory): string => {
    const key = getSpecimenKey(specimen);
    const selected = selectedSpecimens.find(selectedItem => selectedItem.key === key);

    if (!selected) return 'specimen-row';

    const isOverOwnedQuantity = selected.quantity > specimen.quantityOwned;
    const isOverAvailableQuantity = selected.quantity > specimen.quantityAvailable;

    if (isOverOwnedQuantity) {
      showToast.error(toastMessages.CHOSE_MORE_THAN_OWNED);
      return 'specimen-row invalid-row';
    }
    if (isOverAvailableQuantity) {
      return 'specimen-row invalid-row';
    }
    return 'specimen-row';
  };

  const handleLocationRowClass = useCallback(
    (location: SpecimenLocation, key: string): string => {
      const current = locationUseAmounts.find(
        locationAmount => locationAmount.key === key && locationAmount.location.storageCanisterId === location.storageCanisterId,
      );
      if (current && current?.amount > location.quantity) {
        showToast.error(toastMessages.CHOSE_MORE_THAN_TOTAL);
        return 'specimen-row invalid-row';
      } else {
        return 'specimen-row';
      }
    },
    [locationUseAmounts],
  );

  const handleSpecimenLocationAmountChange = (key: string, canisterId: number, newAmount: number) => {
    setLocationUseAmounts(prev =>
      prev.map(location =>
        location.key === key && location.location.storageCanisterId === canisterId
          ? { ...location, amount: newAmount }
          : location,
      ),
    );
  };

  const isSpecimenValid = (specimen: SpecimenInventory) => {
    if (specimenNeedsStrawDetails(specimen)) {
      return true;
    }

    if (isFemale(specimen)) {
      return (specimen.quantityAvailable ?? 0) > 0;
    }
    return true;
  };

  const renderLocationTableRows = (specimen: SpecimenInventory, key: string, location: SpecimenLocation) => {
    const isTravelingTank = location.storageCanister?.storageTank?.isTravelingTank;
    const rowClass = `${handleLocationRowClass(location, key)} ${isTravelingTank ? 'disabled' : ''}`;
    const locationPath = `${location.storageCanister?.storageTank?.storageSite?.name}/${location.storageCanister?.storageTank?.name}/${location.storageCanister?.name}`;
    const animalDetails = `${specimen.animal?.code} - ${dateFormatMMDDYYYY(specimen.freezeDate)}`;
    const currentAmount =
      locationUseAmounts.find(l => l.key === key && l.location.storageCanisterId === location.storageCanisterId)?.amount ?? 0;

    return (
      <tr className={rowClass} key={`${key}-loc-${location?.storageCanisterId}`}>
        <td>{locationPath}</td>
        <td>{animalDetails}</td>
        <td>{location.quantity}</td>

        {hasFemaleSpecimen && <td>{specimen.embryoStrawCode || '-'}</td>}
        <td>
          {isTravelingTank ? (
            LABEL_CONSTANTS.TRAVELING_TANK
          ) : isFemale(specimen) ? (
            <NumberInput value={specimen.quantityAvailable} />
          ) : (
            <NumberInput
              onChange={e => handleSpecimenLocationAmountChange(key, location.storageCanisterId, Number(e.target.value))}
              value={currentAmount.toString()}
            />
          )}
        </td>
      </tr>
    );
  };

  const renderLocationMobileRow = (specimen: SpecimenInventory, key: string, location: SpecimenLocation) => {
    const isTravelingTank = location.storageCanister?.storageTank?.isTravelingTank;
    const locationPath = `${location.storageCanister?.storageTank?.storageSite?.name} / ${location.storageCanister?.storageTank?.name} / ${location.storageCanister?.name}`;
    const animalDetails = `${specimen.animal?.code} - ${dateFormatMMDDYYYY(specimen.freezeDate)}`;
    const currentAmount =
      locationUseAmounts.find(l => l.key === key && l.location.storageCanisterId === location.storageCanisterId)?.amount ?? 0;

    return (
      <div className="selected-storage-location" key={`${key}-loc-${location.storageCanisterId}`}>
        <div className={`card max-width ${isTravelingTank ? 'disabled' : ''}`}>
          <div className="body">
            <div className="body-content">
              <div className="selected-location">
                <label>{TABLE_HEADER_CONSTANTS.LOCATION}:</label>
                <label>{locationPath}</label>
              </div>
              <div className="location-item">
                <label>{TABLE_HEADER_CONSTANTS.ITEM}:</label>
                <label>{animalDetails}</label>
              </div>
            </div>
            <hr />
            <div className="body-content">
              <div className="canister-qty">
                <label>{TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER}</label>
                <label>{location.quantity}</label>
              </div>
              <div className="actual-quantity">
                {isTravelingTank ? (
                  <label>{LABEL_CONSTANTS.TRAVELING_TANK}</label>
                ) : isFemale(specimen) ? (
                  <>
                    <label>{TABLE_HEADER_CONSTANTS.USE_QTY}</label>
                    <NumberInput value={specimen.quantityAvailable} />
                  </>
                ) : (
                  <>
                    <label>{TABLE_HEADER_CONSTANTS.USE_QTY}</label>
                    <NumberInput
                      onChange={e => handleSpecimenLocationAmountChange(key, location.storageCanisterId, Number(e.target.value))}
                      value={currentAmount.toString()}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const toggleSpecimenSelection = (specimen: SpecimenInventory, checked: boolean) => {
    const key = getSpecimenKey(specimen);

    setSelectedSpecimens(prev => {
      if (checked) {
        if (prev.some(selectedItem => selectedItem.key === key)) return prev;

        return [
          ...prev,
          {
            key,
            specimen,
            quantity: isFemale(specimen) ? specimen.quantityAvailable ?? 0 : 0,
          },
        ];
      }
      return prev.filter(selectedItem => selectedItem.key !== key);
    });
  };

  const filteredSpecimens = specimens?.filter(isSpecimenValid) ?? [];

  return (
    <div className="on-farm on-farm-use">
      <BackButton />
      <div className="inventory-action card">
        {isLoading && <Loader pageLoader loaderSize={'medium'} simple />}
        <h1>{NAVIGATION_LINK_CONSTANTS.FARM_USE}</h1>

        <OnFarmAnimalSelector animalId={animalId} actionPath={ON_FARM_PATHS.FARM_USE} />
        <AnimalInfo animalId={animalId} showQuantityAvailable inventorySource={InventorySource.ON_FARM} />
        <br />

        <h2>{LABEL_CONSTANTS.SELECT_ITEM}</h2>
        {animalId && activeAccount?.accountId && (
          <div className="use-select-item">
            <div className="desk-select-item">
              <div className={'specimen-table'}>
                <table>
                  <thead>
                    <tr>
                      <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                      <th>{TABLE_HEADER_CONSTANTS.LOT_DATE_NO}</th>
                      {hasFemaleSpecimen && (
                        <>
                          <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                          <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                        </>
                      )}
                      <th>{TABLE_HEADER_CONSTANTS.OWNED_QTY}</th>
                      <th>{hasFemaleSpecimen ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}</th>
                      <th>{TABLE_HEADER_CONSTANTS.USE_QTY}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!isLoadingSpecimens && filteredSpecimens.length === 0 && (
                      <tr>
                        <td colSpan={hasFemaleSpecimen ? 7 : 5} className="no-records">
                          {LABEL_CONSTANTS.NO_RESULTS_FOUND}
                        </td>
                      </tr>
                    )}

                    {!isLoadingSpecimens &&
                      filteredSpecimens.map(specimen => {
                        const key = getSpecimenKey(specimen);
                        const isSelected = selectedSpecimens.some(selectedSpecimen => selectedSpecimen.key === key);
                        return (
                          <tr className={handleSpecimenRowClass(specimen)} key={`specimen-row-${key}`}>
                            <td>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={specimenNeedsStrawDetails(specimen)}
                                onChange={e => toggleSpecimenSelection(specimen, e.target.checked)}
                              />
                            </td>
                            <td>
                              <SpecimenLotInfo
                                specimenId={specimen.specimenId}
                                lotNumber={specimen?.lotNumber}
                                freezeDate={specimen.freezeDate}
                              />
                            </td>
                            {hasFemaleSpecimen && (
                              <>
                                <td>{renderStrawLotContent(specimen)}</td>

                                <td>{specimen?.embryoStrawNumber ?? '-'}</td>
                              </>
                            )}
                            <td>{specimen.quantityOwned}</td>
                            <td>{specimen.quantityAvailable}</td>
                            <td>
                              {isFemale(specimen) ? (
                                <span>{specimen.quantityAvailable}</span>
                              ) : (
                                <NumberInput
                                  disabled={!isSelected}
                                  value={
                                    selectedSpecimens.find(selectedItem => selectedItem.key === key)?.quantity?.toString() ?? ''
                                  }
                                  onChange={e =>
                                    setSelectedSpecimens(prev =>
                                      prev.map(selectedItem =>
                                        selectedItem.key === key
                                          ? { ...selectedItem, quantity: Number(e.target.value) }
                                          : selectedItem,
                                      ),
                                    )
                                  }
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    {isLoadingSpecimens && (
                      <tr>
                        <td colSpan={6}>
                          <>
                            <br />
                            <br />
                            <Loader loaderSize="small" />
                            <br />
                            <br />
                          </>
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={hasFemaleSpecimen ? 6 : 4}></td>
                      <td colSpan={1}>
                        <div className="quantity total-to-use">{totalSelectedUseQty}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="xs-select-item">
              {filteredSpecimens.map(specimen => {
                const key = getSpecimenKey(specimen);
                const selectedSpecimen = selectedSpecimens.find(s => s.key === key);

                return (
                  <div key={key} className="selected-specimen">
                    <div className={`card max-width ${selectedSpecimen ? 'selected' : ''}`}>
                      <div className="body">
                        <div className="checkbox-column">
                          <input
                            type="checkbox"
                            checked={!!selectedSpecimen}
                            disabled={specimenNeedsStrawDetails(specimen)}
                            onChange={e => toggleSpecimenSelection(specimen, e.target.checked)}
                          />
                        </div>

                        <div className="owned-quantity">
                          <label>{TABLE_HEADER_CONSTANTS.OWNED_QTY}:</label>
                          <label>{specimen.quantityOwned}</label>
                        </div>
                        <div className="available-quantity">
                          <label>{TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}:</label>
                          <label>{specimen.quantityAvailable}</label>
                        </div>
                        <div className="actual-quantity">
                          <label>{TABLE_HEADER_CONSTANTS.USE_QTY}:</label>
                          {isFemale(specimen) ? (
                            <span>{specimen.quantityAvailable}</span>
                          ) : (
                            <NumberInput
                              disabled={!selectedSpecimen}
                              value={selectedSpecimen?.quantity?.toString() ?? ''}
                              onChange={e =>
                                setSelectedSpecimens(prev =>
                                  prev.map(item => (item.key === key ? { ...item, quantity: Number(e.target.value) } : item)),
                                )
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="total-quantity">
                <label>{LABEL_CONSTANTS.TOTAL_QTY_TO_USE} :&nbsp;</label>
                {totalSelectedUseQty}
              </div>
            </div>
          </div>
        )}
        <br />

        <h2>{LABEL_CONSTANTS.ITEM_STORAGE_LOCATIONS}</h2>
        {animalId && activeAccount?.accountId && (
          <div className="use-storage-locations">
            <div className="desk-storage-locations-table">
              <div className="specimen-table" key={childKey}>
                <table>
                  <thead>
                    <tr>
                      <th>{TABLE_HEADER_CONSTANTS.LOCATION}</th>
                      <th>{TABLE_HEADER_CONSTANTS.ITEM}</th>
                      <th>{TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER}</th>
                      {hasFemaleSpecimen && <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>}
                      <th>{TABLE_HEADER_CONSTANTS.USE_QTY}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSpecimens.length === 0 ? (
                      <>
                        <tr>
                          <td colSpan={hasFemaleSpecimen ? 5 : 4} className="storage-locations-placeholder">
                            {LABEL_CONSTANTS.SELECT_SPECIMEN_TO_VIEW_STORAGE_LOCATIONS}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={hasFemaleSpecimen ? 4 : 3}></td>
                          <td colSpan={1}>
                            <div className="quantity storage-total">0</div>
                          </td>
                        </tr>
                      </>
                    ) : (
                      <>
                        {selectedSpecimens.map(({ specimen, key }) =>
                          specimen.specimenLocations?.map(location => renderLocationTableRows(specimen, key, location)),
                        )}
                        <tr className={totalLocationUseAmounts > totalSelectedUseQty ? 'invalid-row' : ''}>
                          <td colSpan={hasFemaleSpecimen ? 4 : 3}></td>
                          <td colSpan={1}>
                            <div className={'quantity storage-total'}>{totalLocationUseAmounts}</div>
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="xs-storage-locations-table">
              {selectedSpecimens.length === 0 ? (
                <div className="storage-locations-empty-card">
                  <p>{LABEL_CONSTANTS.SELECT_SPECIMEN_TO_VIEW_STORAGE_LOCATIONS}</p>
                </div>
              ) : (
                <>
                  {selectedSpecimens.map(({ specimen, key }) =>
                    specimen.specimenLocations?.map(location => renderLocationMobileRow(specimen, key, location)),
                  )}
                </>
              )}
              <div className="storage-total">
                <label>{LABEL_CONSTANTS.SELECTED_QTY_TO_USE}:&nbsp;</label>
                {totalLocationUseAmounts}
              </div>
            </div>
          </div>
        )}
        <br />

        <div className="form-row">
          <label>
            <div>
              *{LABEL_CONSTANTS.BREED_DATE}:
              <RequireAlert isActive={isValidationAlertActive && !breedDate} />
            </div>
          </label>
          <div className="input-container">
            <DateInput value={breedDate} onChange={setBreedDate} isClearable minDate={new Date()} maxDate={null} />
          </div>
        </div>

        <div className="form-row expanded-text-area">
          <label htmlFor="notes">{LABEL_CONSTANTS.NOTES}</label>
          <textarea
            id="notes"
            name="notes"
            placeholder={LABEL_CONSTANTS.NOTES}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={4}
            cols={50}
          />
        </div>
        <br />
        <div className="submit-button">
          <button onClick={handleSubmit} className={`button green small ${!isValid() && 'disabled'}`}>
            {BUTTON_CONSTANTS.SUBMIT}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnFarmUse;
